import { $t } from '@/i18n';
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch } from '@vue/composition-api';
import monacoProvider from '@utils/monacoProvider';
import styles from './styles/code-conflict-resolved-view.module.scss';
import useEditConflict from './hooks/useEditConflict';
import screenInfo from '@/utils/screen';
/**
 * 代码冲突已解决视图组件
 * 用于展示已解决冲突的 before/after 差异对比
 *
 * 功能特性：
 * - Monaco Diff Editor 双栏对比
 * - 自动导航到第一个差异
 * - 支持 YAML/JSON 语法高亮
 * - 显示当前分支和合并分支信息
 */
export default defineComponent({
    name: 'CodeConflictResolvedView',
    props: {
        originalData: {
            type: String,
            required: true,
            default: '',
        },
        modifiedData: {
            type: String,
            required: true,
            default: '',
        },
        type: {
            type: String,
            default: 'yaml',
        },
        currentBranch: {
            type: String,
            default: '',
        },
        mergeBranch: {
            type: String,
            default: '',
        },
        readonly: {
            type: Boolean,
            default: true,
        },
    },
    setup(props) {
        const diffContainer = ref(null);
        const modifiedModel = ref();
        const diffEditor = ref();
        const originalModel = ref(null);
        const resizeObserver = ref();
        const editor = computed(() => diffEditor.value?.getModifiedEditor());
        const isCompact = computed(() => screenInfo.isCompactViewport);
        const { updateConflicts, } = useEditConflict({ editor, currentLabel: $t('k_ceabce69'), incomingLabel: $t('k_773d8833') });
        const updateDiff = () => {
            monacoProvider((monaco) => {
                originalModel.value = monaco.editor.createModel(props.originalData, props.type);
                modifiedModel.value = monaco.editor.createModel(props.modifiedData, props.type);
                diffEditor.value.setModel({
                    original: originalModel.value,
                    modified: modifiedModel.value,
                });
                const navi = monaco.editor.createDiffNavigator(diffEditor.value, {
                    followsCaret: true,
                    ignoreCharChanges: true,
                });
                navi.next();
                addListeners();
            });
        };
        const addListeners = () => {
            const modifiedEditor = diffEditor.value.getModifiedEditor();
            modifiedEditor.onDidChangeModelContent(() => {
                updateConflicts(false);
            });
        };
        // 监听数据变化
        watch(() => props.originalData, () => {
            updateDiff();
        });
        watch(() => props.modifiedData, () => {
            updateDiff();
        });
        watch(isCompact, (compact) => {
            diffEditor.value?.updateOptions({ renderSideBySide: !compact });
        });
        onMounted(() => {
            monacoProvider((monaco) => {
                diffEditor.value = monaco.editor.createDiffEditor(diffContainer.value, {
                    enableSplitViewResizing: false,
                    renderSideBySide: !isCompact.value,
                    readOnly: props.readonly,
                    foldingStrategy: 'indentation',
                    cursorBlinking: 'solid',
                    automaticLayout: true,
                    tabSize: 2,
                    language: 'javascript',
                });
                updateDiff();
                // monaco有bug，滚动条不显示导致滚动条失效，js强制修改style
                setTimeout(() => {
                    const scrollbars = document.getElementsByClassName('visible scrollbar vertical');
                    for (const scrollbar of scrollbars) {
                        scrollbar.style.display = 'block';
                        scrollbar.style.height = '18px';
                    }
                }, 500);
                updateConflicts(true);
                if (diffContainer.value) {
                    const observer = new ResizeObserver(() => {
                        diffEditor.value?.layout();
                    });
                    observer.observe(diffContainer.value);
                    resizeObserver.value = observer;
                }
            });
        });
        onBeforeUnmount(() => {
            resizeObserver.value?.disconnect();
            diffEditor.value?.dispose();
        });
        return {
            diffContainer,
            modifiedModel,
            diffEditor,
            isCompact,
            updateConflicts,
        };
    },
    render() {
        return (<div class={styles.resolvedRoot}>
        <div class={[styles.diff_header, this.isCompact ? styles.diff_header_compact : null]}>
          <div>{$t('k_5f4086f5')}{this.currentBranch}</div>
          <div>{$t('k_abcc76ad')}{this.mergeBranch}</div>
        </div>
        <div class={styles.diffHost}>
          <div ref="diffContainer" class={styles.diffEditor}/>
        </div>
      </div>);
    },
});
